import React from 'react';
import { useNavigate } from 'react-router-dom';
import PublicNav from './PublicNav';
import PublicFooter from './PublicFooter';
import './TechnologyPage.css';

const TechnologyPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="technology-page">
      {/* Navigation Header */}
      <PublicNav onLoginClick={() => navigate('/login')} />

      <div className="tech-container">
        <header className="tech-header">
          <h1>Technology & Architecture</h1>
          <p className="tech-subtitle">Understanding the hybrid blockchain architecture, smart contracts, and how assets are digitized</p>
        </header>

        {/* Core Architecture */}
        <section className="tech-section tech-section-core">
          <div className="section-header">
            <div className="section-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              </svg>
            </div>
            <h2>Core Architecture</h2>
          </div>
          <p className="section-intro">
            This platform uses a <strong>hybrid on-chain/off-chain architecture</strong> to digitize ownership of private assets 
            while maintaining legal compliance and privacy. Understanding this separation is key to understanding how the system works.
          </p>

          <div className="concept-grid">
            <div className="concept-item concept-item-principles">
              <div className="concept-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                  <path d="M2 17l10 5 10-5"></path>
                  <path d="M2 12l10 5 10-5"></path>
                </svg>
              </div>
              <h3>Design Principles</h3>
              <ul>
                <li><strong>Private ≠ Public:</strong> Transparency does not imply tradability. These tokens are not for public markets.</li>
                <li><strong>Legal Reality First:</strong> On-chain records reflect, but do not replace, legal agreements.</li>
                <li><strong>Clarity Over Complexity:</strong> The system favors understandable rules over financial engineering.</li>
                <li><strong>Deterministic Rules:</strong> Ownership logic is enforced by code where possible.</li>
                <li><strong>Hybrid by Design:</strong> Some truths belong on-chain; others must remain off-chain.</li>
              </ul>
            </div>

            <div className="concept-item concept-item-not">
              <div className="concept-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
              </div>
              <h3>What This Is Not</h3>
              <p>This platform is explicitly <strong>not</strong>:</p>
              <ul>
                <li>A public exchange or trading platform</li>
                <li>A security offering mechanism</li>
                <li>A price discovery mechanism</li>
                <li>A DeFi protocol</li>
                <li>An anonymous system</li>
              </ul>
              <p className="tech-note">
                It is an <strong>ownership infrastructure</strong>, not a market. Tokens represent ownership units, not currency.
              </p>
            </div>
          </div>
        </section>

        {/* Hybrid Architecture */}
        <section className="tech-section tech-section-hybrid">
          <div className="section-header">
            <div className="section-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
              </svg>
            </div>
            <h2>Hybrid Architecture: On-Chain vs Off-Chain</h2>
          </div>
          <p className="section-intro">
            The platform separates data and operations into two layers based on what needs to be immutable and auditable 
            versus what needs to remain private and legally compliant.
          </p>

          <div className="hybrid-grid">
            <div className="hybrid-item hybrid-item-offchain">
              <div className="hybrid-header">
                <div className="hybrid-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </div>
                <h3>Off-Chain Layer (Legal & Identity)</h3>
              </div>
              <p className="hybrid-purpose"><strong>Purpose:</strong> Handles identity, legal compliance, and private information</p>
              <ul>
                <li><strong>User Identities:</strong> Email addresses, names, roles (stored privately)</li>
                <li><strong>Authentication:</strong> Email-based OTP login (no passwords)</li>
                <li><strong>Legal Documents:</strong> Corporate documents, agreements, KYC/AML records</li>
                <li><strong>Vesting Grants:</strong> Grant definitions, schedules, cliff periods</li>
                <li><strong>Distribution Declarations:</strong> When distributions are declared, amounts, record dates</li>
                <li><strong>Payment Tracking:</strong> Whether distributions have been paid (off-chain settlement)</li>
                <li><strong>Access Control:</strong> Who can see what, role-based permissions</li>
              </ul>
              <p className="tech-note">
                <strong>Why Off-Chain:</strong> This data is private, requires legal compliance, and may need to be updated 
                or corrected. It's governed by corporate law and contract law, not blockchain consensus.
              </p>
            </div>

            <div className="hybrid-item hybrid-item-onchain">
              <div className="hybrid-header">
                <div className="hybrid-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                    <line x1="3" y1="9" x2="21" y2="9"></line>
                    <line x1="9" y1="21" x2="9" y2="9"></line>
                  </svg>
                </div>
                <h3>On-Chain Layer (Ownership & Rules)</h3>
              </div>
              <p className="hybrid-purpose"><strong>Purpose:</strong> Immutable ownership records and deterministic rule enforcement</p>
              <ul>
                <li><strong>Token Balances:</strong> How many ownership units each wallet holds</li>
                <li><strong>Total Supply:</strong> Total number of units issued for an asset</li>
                <li><strong>Whitelist:</strong> Which wallet addresses are approved to hold tokens</li>
                <li><strong>Transfer Restrictions:</strong> Whether transfers are enabled or disabled</li>
                <li><strong>Minting Events:</strong> When new units are created and allocated</li>
                <li><strong>Burning Events:</strong> When units are destroyed</li>
                <li><strong>Administrative Actions:</strong> Whitelist changes, transfer toggles (all logged)</li>
              </ul>
              <p className="tech-note">
                <strong>Why On-Chain:</strong> This data needs to be immutable, auditable, and serve as a shared source of truth. 
                Once recorded on the blockchain, it cannot be altered, providing transparency and trust.
              </p>
            </div>
          </div>
        </section>

        {/* Smart Contracts */}
        <section className="tech-section tech-section-contracts">
          <div className="section-header">
            <div className="section-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
              </svg>
            </div>
            <h2>Smart Contracts: The Technical Foundation</h2>
          </div>
          <p className="section-intro">
            Each asset is represented by a <strong>restricted ERC-20 token contract</strong> deployed on the blockchain. 
            This contract functions as a digital share register, enforcing ownership rules programmatically.
          </p>

          <div className="contract-details">
            <h3>Token Contract Structure</h3>
            <div className="code-block">
              <pre>{`Each Asset = One Smart Contract

Key Characteristics:
- ERC-20 compliant (standard token interface)
- Whitelist-based access control
- Transfer restrictions (enabled/disabled)
- Minting and burning capabilities
- Administrative role management
- Event emission for transparency`}</pre>
            </div>

            <h4>Core Functions</h4>
            <div className="function-list">
              <div className="function-item">
                <code>mint(address to, uint256 amount)</code>
                <p>Creates new ownership units and assigns them to a whitelisted wallet address. Only administrators can call this.</p>
              </div>
              <div className="function-item">
                <code>burn(uint256 amount)</code>
                <p>Destroys ownership units from the caller's balance. Reduces total supply.</p>
              </div>
              <div className="function-item">
                <code>addToWhitelist(address account)</code>
                <p>Adds a wallet address to the whitelist, allowing it to receive and hold tokens.</p>
              </div>
              <div className="function-item">
                <code>removeFromWhitelist(address account)</code>
                <p>Removes a wallet from the whitelist. The wallet can no longer receive new tokens.</p>
              </div>
              <div className="function-item">
                <code>enableTransfers() / disableTransfers()</code>
                <p>Toggles whether token transfers between whitelisted addresses are allowed. Disabled by default.</p>
              </div>
              <div className="function-item">
                <code>transfer() / transferFrom()</code>
                <p>Overridden ERC-20 functions that enforce whitelist checks and transfer restrictions before allowing movement.</p>
              </div>
            </div>

            <h4>Security Features</h4>
            <ul>
              <li><strong>Access Control:</strong> Uses OpenZeppelin's AccessControl for role-based permissions (only admins can mint/manage)</li>
              <li><strong>Whitelist Enforcement:</strong> All token operations require addresses to be whitelisted first</li>
              <li><strong>Transfer Restrictions:</strong> Transfers are disabled by default and can only be enabled by administrators</li>
              <li><strong>Reentrancy Protection:</strong> Guards against reentrancy attacks on critical functions</li>
              <li><strong>Event Emission:</strong> All actions emit events that can be indexed and monitored off-chain</li>
            </ul>
          </div>
        </section>

        {/* Process Flows */}
        <section className="tech-section tech-section-processes">
          <div className="section-header">
            <div className="section-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
              </svg>
            </div>
            <h2>Process Flows: How It Works Technically</h2>
          </div>
          <p className="section-intro">
            Understanding the step-by-step processes helps clarify how assets are digitized and how ownership is managed.
          </p>

          <div className="process-item">
            <h3>1. Asset Digitization Process</h3>
            <p>How a real-world asset becomes a tokenized asset on the blockchain:</p>
            <ol>
              <li><strong>Asset Creation:</strong> Administrator creates an asset record (company, property, fund, etc.)</li>
              <li><strong>Smart Contract Deployment:</strong> A new ERC-20 token contract is deployed to the blockchain</li>
              <li><strong>Contract Registration:</strong> The contract address is linked to the asset record</li>
              <li><strong>Total Supply Definition:</strong> Administrator sets the total number of ownership units</li>
              <li><strong>Whitelist Setup:</strong> Approved wallet addresses are added to the contract's whitelist</li>
              <li><strong>Initial Allocation:</strong> Ownership units are minted to whitelisted addresses</li>
            </ol>
            <p className="tech-note">
              <strong>Result:</strong> The asset now exists on-chain as a token contract. Ownership is represented by token balances 
              in whitelisted wallets. The contract address serves as the unique identifier for the asset.
            </p>
          </div>

          <div className="process-item">
            <h3>2. Ownership Allocation Process</h3>
            <p>How ownership units are assigned to holders:</p>
            <ol>
              <li><strong>Holder Identification:</strong> Administrator identifies who should receive ownership (by email/user account)</li>
              <li><strong>Wallet Linking:</strong> Holder connects their wallet address to their account</li>
              <li><strong>Whitelist Addition:</strong> Administrator adds the wallet address to the contract whitelist (on-chain transaction)</li>
              <li><strong>Token Minting:</strong> Administrator mints the specified number of units to the whitelisted address (on-chain transaction)</li>
              <li><strong>Balance Update:</strong> The blockchain records the new balance, visible to anyone</li>
              <li><strong>Off-Chain Record:</strong> The platform links the on-chain balance to the user's identity off-chain</li>
            </ol>
            <p className="tech-note">
              <strong>Key Point:</strong> The actual ownership is recorded on-chain (immutable), but the link between wallet address 
              and user identity is stored off-chain (private). This provides transparency of ownership while maintaining privacy of identity.
            </p>
          </div>

          <div className="process-item">
            <h3>3. Vesting Process</h3>
            <p>How employee equity vesting works:</p>
            <ol>
              <li><strong>Grant Creation:</strong> Administrator creates a vesting grant off-chain (defines total units, start date, cliff, duration)</li>
              <li><strong>Vesting Calculation:</strong> Platform calculates vested amount using deterministic formula:
                <div className="formula-box">
                  <code>vested = (elapsed_time / total_duration) × grant_amount</code>
                  <p>Cliff period enforced: no vesting until cliff expires</p>
                </div>
              </li>
              <li><strong>Real-Time Display:</strong> Holder sees their vested vs unvested units in real-time</li>
              <li><strong>Token Minting:</strong> Administrator mints tokens as they vest (can be done periodically or all at once)</li>
              <li><strong>On-Chain Balance:</strong> Minted tokens appear in holder's wallet balance</li>
            </ol>
            <p className="tech-note">
              <strong>Important:</strong> Vesting calculations happen off-chain for clarity and flexibility. The actual tokens 
              are minted on-chain when administrators choose to do so. This separation allows for complex vesting schedules 
              while keeping on-chain operations simple.
            </p>
          </div>

          <div className="process-item">
            <h3>4. Distribution Process</h3>
            <p>How dividends and profit distributions work:</p>
            <ol>
              <li><strong>Distribution Declaration:</strong> Administrator declares a distribution off-chain (amount, currency, record date)</li>
              <li><strong>Ownership Snapshot:</strong> Platform takes a snapshot of all token balances at the record date</li>
              <li><strong>Pro-Rata Calculation:</strong> Each holder's entitlement is calculated:
                <div className="formula-box">
                  <code>entitlement = (holder_units / total_supply) × distribution_amount</code>
                </div>
              </li>
              <li><strong>Entitlement Recording:</strong> Each holder's calculated entitlement is recorded off-chain</li>
              <li><strong>Payment Tracking:</strong> Administrator marks entitlements as paid as payments are made (off-chain settlement)</li>
              <li><strong>Optional On-Chain Anchor:</strong> Distribution can optionally be recorded on-chain for audit purposes</li>
            </ol>
            <p className="tech-note">
              <strong>Key Point:</strong> Distributions are calculated based on on-chain ownership snapshots (transparent and auditable), 
              but actual payment settlement happens off-chain (flexible, can use bank transfers, checks, etc.). The platform tracks 
              what's owed vs what's been paid.
            </p>
          </div>

          <div className="process-item">
            <h3>5. Wallet Recovery Process</h3>
            <p>What happens if someone loses access to their wallet:</p>
            <ol>
              <li><strong>Identity Verification:</strong> Holder verifies their identity through email/account system</li>
              <li><strong>New Wallet Setup:</strong> Holder connects a new wallet address</li>
              <li><strong>Whitelist Update:</strong> Administrator adds new wallet to whitelist</li>
              <li><strong>Token Transfer:</strong> Administrator transfers tokens from old wallet to new wallet (if transfers enabled) or mints to new wallet</li>
              <li><strong>Old Wallet Removal:</strong> Old wallet removed from whitelist</li>
            </ol>
            <p className="tech-note">
              <strong>Security:</strong> Recovery requires administrator action and identity verification. This ensures tokens 
              can't be stolen, but legitimate recovery is possible. The on-chain transaction history provides a complete audit trail.
            </p>
          </div>
        </section>

        {/* Token Model */}
        <section className="tech-section tech-section-token">
          <div className="section-header">
            <div className="section-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="1" x2="12" y2="23"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
            </div>
            <h2>Token Model: Restricted Ownership Tokens</h2>
          </div>
          <p className="section-intro">
            These are not typical cryptocurrency tokens. They are <strong>restricted ownership tokens</strong> designed specifically 
            for private asset representation.
          </p>

          <div className="token-model">
            <h3>Key Characteristics</h3>
            <ul>
              <li><strong>One Contract Per Asset:</strong> Each asset (company, property, fund) has its own token contract</li>
              <li><strong>Units, Not Currency:</strong> Tokens represent ownership units, not monetary value</li>
              <li><strong>Whitelist Only:</strong> Only approved wallet addresses can hold tokens</li>
              <li><strong>Non-Transferable by Default:</strong> Transfers are disabled unless administrators enable them</li>
              <li><strong>Administrative Control:</strong> Only administrators can mint, burn, and manage whitelists</li>
              <li><strong>Public Balances:</strong> Token balances are publicly visible on the blockchain</li>
              <li><strong>Private Identity:</strong> The link between wallet address and user identity is private (off-chain)</li>
            </ul>

            <h3>Why This Model?</h3>
            <p>
              This model provides <strong>transparency</strong> (anyone can verify ownership balances) while maintaining 
              <strong> privacy</strong> (identities are not public) and <strong>control</strong> (only approved parties can participate). 
              It's designed for private markets, not public trading.
            </p>
          </div>
        </section>

        {/* Blockchain Network */}
        <section className="tech-section tech-section-network">
          <div className="section-header">
            <div className="section-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                <line x1="12" y1="22.08" x2="12" y2="12"></line>
              </svg>
            </div>
            <h2>Blockchain Network & Infrastructure</h2>
          </div>
          <p className="section-intro">
            The platform uses Ethereum-compatible blockchain networks. Understanding the network helps understand how transactions work.
          </p>

          <div className="network-info">
            <h3>Network Details</h3>
            <ul>
              <li><strong>Ethereum-Compatible:</strong> Works on Ethereum mainnet, Sepolia testnet, or any EVM-compatible chain</li>
              <li><strong>Smart Contracts:</strong> Written in Solidity, compiled and deployed to the network</li>
              <li><strong>Gas Fees:</strong> Administrators pay gas fees (in ETH or network tokens) for on-chain transactions</li>
              <li><strong>Transaction Finality:</strong> Transactions are confirmed and immutable once included in a block</li>
              <li><strong>Public Ledger:</strong> All transactions are publicly visible and auditable</li>
            </ul>

            <h3>What Users Need to Know</h3>
            <ul>
              <li><strong>Wallet Required:</strong> Users need an Ethereum-compatible wallet (like MetaMask) to view on-chain balances</li>
              <li><strong>Network Selection:</strong> Wallet must be connected to the correct network (testnet vs mainnet)</li>
              <li><strong>Read-Only Access:</strong> Most users only need read access to view balances (no gas fees)</li>
              <li><strong>Administrative Actions:</strong> Only administrators perform write operations (minting, whitelisting) and pay gas fees</li>
            </ul>
          </div>
        </section>
      </div>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
};

export default TechnologyPage;
